import { useMemo, useState } from "react";
import { ReadOnlyPill } from "@/components/auth/ReadOnlyPill";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Loader2 } from "lucide-react";
import { useWorkOrders } from "@/features/work-orders/api";
import { SeverityBadge, WoStatusBadge } from "@/features/work-orders/badges";
import { KANBAN_COLUMNS, WO_STATUSES, type WoSeverity, type WoStatus, type WoType, type WorkOrderRow } from "@/features/work-orders/types";
import { formatDuration, minutesBetween, timeAgo } from "@/features/work-orders/format";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isDeptScopedOnly, canExportMaintenanceReports } from "@/lib/roles";
import { useBranch } from "@/context/BranchContext";
import { useConfigOptions, useConfigStatuses } from "@/features/configuration/api";
import { ListExportButtons } from "@/features/reports/ListExportButtons";

export const Route = createFileRoute("/_authenticated/work-orders/")({
  component: WorkOrdersPage,
});

function WorkOrdersPage() {
  const navigate = useNavigate();
  const { data: me } = useCurrentUser();
  const deptScopedOnly = isDeptScopedOnly(me?.roles);
  const { currentBranchId, canSwitch, lockedBranchId, branches, setCurrentBranchId } = useBranch();
  const effectiveBranchId = lockedBranchId ?? currentBranchId ?? null;
  const [view, setView] = useState<"kanban" | "table">("kanban");
  const [mine, setMine] = useState(false);
  const effectiveMine = deptScopedOnly ? false : mine;
  const [search, setSearch] = useState("");
  const [severity, setSeverity] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [type, setType] = useState<WoType | "">("");

  // Dynamic config: priority options + statuses (fallback to hardcoded on empty/error)
  const { data: priorityOptions } = useConfigOptions("work_orders", "priority");
  const { data: configStatuses } = useConfigStatuses("work_orders");

  const severityOptions = useMemo(() => {
    const active = (priorityOptions ?? []).filter((o) => o.is_active);
    if (active.length > 0) {
      return active
        .slice()
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((o) => ({ value: o.value || o.label, label: o.label }));
    }
    return (["Critical", "Major", "Minor"] as WoSeverity[]).map((s) => ({ value: s, label: s }));
  }, [priorityOptions]);

  const statusOptions = useMemo(() => {
    const active = (configStatuses ?? []).filter((s) => s.is_active);
    if (active.length > 0) {
      return active
        .slice()
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((s) => ({ value: s.status_key, label: s.status_label }));
    }
    return WO_STATUSES.map((s) => ({ value: s, label: s }));
  }, [configStatuses]);

  const { data: rows = [], isLoading } = useWorkOrders({
    search, branchId: effectiveBranchId,
    severity: (severity || null) as WoSeverity | null,
    status: (status || null) as WoStatus | null,
    type: (type || null) as WoType | null,
    mine: effectiveMine, myUserId: me?.user.id ?? null,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Work Orders</h1> <ReadOnlyPill module="work_orders" />
          <p className="text-sm text-muted-foreground">Breakdown maintenance & service tickets.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ListExportButtons
            rows={() =>
              rows.map((r) => ({
                "WO Number": r.wo_number,
                "Ticket Number": r.source_ticket?.ticket_no ?? "",
                Type: r.type,
                Asset: r.asset?.name ?? "",
                Severity: r.severity,
                Status: r.status,
                "Assigned To": r.assignee?.full_name || r.assignee?.email || "",
                "Reported At": r.reported_at,
                "Due Date": r.due_date ?? "",
                "Resolved At": r.resolved_at ?? "",
                "Closed At": r.closed_at ?? "",
                "Downtime (min)": r.downtime_minutes ?? minutesBetween(r.downtime_start ?? r.reported_at, r.downtime_end ?? (["Resolved", "Verified & Closed", "Cancelled"].includes(r.status) ? (r.closed_at ?? r.resolved_at) : new Date().toISOString())) ?? "",
              }))
            }
            filename="Work_Order_Report"
            sheetName="Work Orders"
            disabled={isLoading || rows.length === 0}
            canExport={canExportMaintenanceReports(me?.roles)}
          />
          {/* Phase 7: breakdown creation replaced by Unified Ticketing System. */}

        </div>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-center gap-2 p-3">
          <Input placeholder="Search WO# or description" value={search} onChange={(e) => setSearch(e.target.value)} className="w-56" />
          <Select value={type} onValueChange={(v) => setType(v === "all" ? "" : v as WoType)}>
            <SelectTrigger className="w-32"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All types</SelectItem><SelectItem value="Breakdown">Breakdown</SelectItem><SelectItem value="PM">PM</SelectItem><SelectItem value="Calibration">Calibration</SelectItem></SelectContent>
          </Select>
          <Select value={severity} onValueChange={(v) => setSeverity(v === "all" ? "" : v)}>
            <SelectTrigger className="w-36"><SelectValue placeholder="Severity" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All severities</SelectItem>
              {severityOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
            <SelectTrigger className="w-44"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {statusOptions.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {canSwitch && branches.length > 0 && (
            <Select value={currentBranchId ?? undefined} onValueChange={(v) => setCurrentBranchId(v)}>
              <SelectTrigger className="w-44"><SelectValue placeholder="Branch" /></SelectTrigger>
              <SelectContent>
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {deptScopedOnly ? (
            <span className="ml-auto text-xs text-muted-foreground">Showing work orders for your department.</span>
          ) : (
            <label className="ml-auto flex items-center gap-2 text-sm">
              <Switch checked={mine} onCheckedChange={setMine} /> My WOs
            </label>
          )}
          <Tabs value={view} onValueChange={(v) => setView(v as "kanban" | "table")}>
            <TabsList><TabsTrigger value="kanban">Kanban</TabsTrigger><TabsTrigger value="table">Table</TabsTrigger></TabsList>
          </Tabs>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : view === "kanban" ? <Kanban rows={rows} columns={statusOptions} /> : <TableView rows={rows} />}
    </div>
  );
}

function Kanban({ rows, columns }: { rows: WorkOrderRow[]; columns: { value: string; label: string }[] }) {
  const effectiveCols = useMemo(() => {
    if (columns && columns.length > 0) {
      return columns.map((c) => ({ title: c.label, statuses: [c.value] }));
    }
    return KANBAN_COLUMNS.map((c) => ({ title: c.title, statuses: c.statuses as string[] }));
  }, [columns]);

  const byCol = useMemo(() => {
    const map = new Map<string, WorkOrderRow[]>();
    for (const col of effectiveCols) map.set(col.title, []);
    for (const r of rows) {
      const col = effectiveCols.find((c) => c.statuses.includes(r.status));
      if (col) map.get(col.title)!.push(r);
    }
    return map;
  }, [rows, effectiveCols]);

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-6">
      {effectiveCols.map((col) => (
        <div key={col.title} className="space-y-2">
          <div className="flex items-center justify-between text-sm font-medium">
            <span>{col.title}</span>
            <span className="rounded bg-muted px-1.5 text-xs">{byCol.get(col.title)?.length ?? 0}</span>
          </div>
          <div className="space-y-2 rounded-md bg-muted/40 p-2 min-h-[120px]">
            {(byCol.get(col.title) ?? []).map((r) => (
              <Link key={r.id} to="/work-orders/$woId" params={{ woId: r.id }} className="block">
                <Card className="hover:border-primary transition-colors overflow-hidden">
                  <CardContent className="space-y-2 p-3">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-mono text-xs truncate max-w-full">{r.wo_number}</span>
                      <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${r.type === "PM" ? "bg-blue-500/15 text-blue-700 dark:text-blue-300" : r.type === "Calibration" ? "bg-purple-500/15 text-purple-700 dark:text-purple-300" : "bg-orange-500/15 text-orange-700 dark:text-orange-300"}`}>{r.type}</span>
                      <span className="shrink-0"><SeverityBadge value={r.severity} /></span>
                      {r.source_ticket && (
                        <span className="shrink-0 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-mono text-emerald-700 dark:text-emerald-300">T#{r.source_ticket.ticket_no}</span>
                      )}
                    </div>
                    <div className="text-sm font-medium leading-snug line-clamp-2 break-words">{r.asset?.name ?? "—"}</div>
                    <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                      <span className="truncate min-w-0">{r.assignee?.full_name || r.assignee?.email || "Unassigned"}</span>
                      <span className="shrink-0">{timeAgo(r.reported_at)}</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function TableView({ rows }: { rows: WorkOrderRow[] }) {
  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>WO No</TableHead><TableHead>Ticket</TableHead><TableHead>Type</TableHead><TableHead>Asset</TableHead><TableHead>Severity</TableHead>
              <TableHead>Status</TableHead><TableHead>Assigned To</TableHead>
              <TableHead>Reported</TableHead><TableHead>Due</TableHead><TableHead>Downtime</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={10} className="py-8 text-center text-sm text-muted-foreground">No work orders.</TableCell></TableRow>}
            {rows.map((r) => {
              const isClosedOrResolved = ["Resolved", "Verified & Closed", "Cancelled"].includes(r.status);
              const effEnd = r.downtime_end ?? (isClosedOrResolved ? (r.closed_at ?? r.resolved_at) : new Date().toISOString());
              const effStart = r.downtime_start ?? r.reported_at;
              const dt = r.downtime_minutes ?? minutesBetween(effStart, effEnd);
              return (
                <TableRow key={r.id} className="cursor-pointer" onClick={() => location.assign(`/work-orders/${r.id}`)}>
                  <TableCell className="font-mono text-xs">{r.wo_number}</TableCell>
                  <TableCell className="text-xs">
                    {r.source_ticket ? (
                      <Link to="/tickets/$ticketId" params={{ ticketId: r.source_ticket.id }} className="font-mono text-primary hover:underline" onClick={(e) => e.stopPropagation()}>
                        {r.source_ticket.ticket_no}
                      </Link>
                    ) : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell><span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${r.type === "PM" ? "bg-blue-500/15 text-blue-700 dark:text-blue-300" : r.type === "Calibration" ? "bg-purple-500/15 text-purple-700 dark:text-purple-300" : "bg-orange-500/15 text-orange-700 dark:text-orange-300"}`}>{r.type}</span></TableCell>
                  <TableCell>{r.asset?.name ?? "—"}</TableCell>
                  <TableCell><SeverityBadge value={r.severity} /></TableCell>
                  <TableCell><WoStatusBadge value={r.status} /></TableCell>
                  <TableCell>{r.assignee?.full_name || r.assignee?.email || "—"}</TableCell>
                  <TableCell className="text-sm">{timeAgo(r.reported_at)}</TableCell>
                  <TableCell className="text-sm">{r.due_date ?? "—"}</TableCell>
                  <TableCell className="text-sm">{formatDuration(dt)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
